import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../utils/axios';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Search,
  Shield,
  Eye,
  Bot,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Globe,
} from 'lucide-react';
import { notifications } from '../lib/notifications';
import { playCompletionSound } from '../lib/sound';

const WebsiteChecks = () => {
  const { user } = useAuth();
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState({
    scraping: 0,
    seoAnalysis: 0,
    llmCrawlScore: 0,
    securityCheck: 0,
  });
  const [currentStep, setCurrentStep] = useState('');
  const [analysisData, setAnalysisData] = useState(null);

  // Redirect to login if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-4">Please log in to access the Website Checks tool.</p>
          <Link to="/login">
            <Button>Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  const handleAnalyze = async () => {
    if (!websiteUrl.trim()) return;

    // Check if user is authenticated
    const token = localStorage.getItem('auth_token');
    if (!token) {
      notifications.error('Authentication required', 'Please log in to use this feature');
      return;
    }

    // Format URL - add https:// if no protocol is specified
    let formattedUrl = websiteUrl.trim();

    // Basic URL validation
    if (!formattedUrl) {
      notifications.error('Invalid URL', 'Please enter a website URL');
      return;
    }



    // Add https:// if no protocol is specified
    if (!formattedUrl.match(/^https?:\/\//i)) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // Add www. if it's a bare domain (optional, but helps with some sites)
    if (formattedUrl.match(/^https?:\/\/[^/]+\.[^/]+$/)) {
      // It's a bare domain, let's keep it as is for now
      // Some sites work better without www, others need it
    }

    setIsAnalyzing(true);
    setShowResults(false);
    setAnalysisData(null);

    // Show start notification
    notifications.info('Website checks started', `Analyzing ${formattedUrl}...`);

    // Initialize progress tracking
    setResults({ scraping: 0, seoAnalysis: 0, llmCrawlScore: 0, securityCheck: 0 });
    setShowResults(true);

    const stages = [
      {
        key: 'scraping',
        duration: 2000,
        name: 'Website Scraping',
        step: 'Scraping website content and structure...',
      },
      {
        key: 'seoAnalysis',
        duration: 1800,
        name: 'SEO Analysis',
        step: 'Analyzing meta tags, keywords, and SEO factors...',
      },
      {
        key: 'llmCrawlScore',
        duration: 2200,
        name: 'LLM Crawl Score',
        step: 'Comparing with llms.txt and calculating AI readiness...',
      },
      {
        key: 'securityCheck',
        duration: 1500,
        name: 'Security Check',
        step: 'Scanning for XSS vulnerabilities and security issues...',
      },
    ];

    try {
      // Start the API call using axios (which handles auth automatically)
      const apiPromise = axios.post('/website-checks', { url: formattedUrl });

      // Simulate progressive analysis while API is running
      let currentStageIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentStageIndex < stages.length) {
          const stage = stages[currentStageIndex];
          setCurrentStep(stage.step);

          // Simulate progress for current stage
          const progress = Math.min(100, Math.random() * 30 + 70); // Random progress between 70-100
          setResults((prev) => ({
            ...prev,
            [stage.key]: Math.floor(progress),
          }));

          currentStageIndex++;
        }
      }, 1000);

      // Wait for API response
      const response = await apiPromise;
      clearInterval(progressInterval);

      const data = response.data;

      if (!data.success) {
        throw new Error(data.message || 'Analysis failed');
      }

      // Update with real results
      setResults({
        scraping: data.results.scraping?.score ?? 0,
        seoAnalysis: data.results.seoAnalysis?.score ?? 0,
        llmCrawlScore: data.results.llmCrawlScore?.score ?? 0,
        securityCheck: data.results.securityCheck?.score ?? 0,
      });

      setAnalysisData(data.results);
      setCurrentStep('Analysis complete!');

      // Show completion notifications for each stage
      stages.forEach((stage) => {
        const score = data.results[stage.key]?.score ?? 0;
        notifications.success(
          `${stage.name} complete`,
          `Score: ${score}%`
        );
      });

      // Show final completion notification and play sound
      notifications.success(
        'Website checks complete!',
        'Check the results and improvement suggestions below.'
      );
      playCompletionSound();

    } catch (error) {
      notifications.error('Analysis failed', error.message);
      playCompletionSound();
      console.error('Analysis error:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreStatus = (score) => {
    if (score >= 90)
      return {
        icon: CheckCircle2,
        color: 'text-green-600',
        bg: 'bg-green-100',
      };
    if (score >= 70)
      return {
        icon: AlertTriangle,
        color: 'text-yellow-600',
        bg: 'bg-yellow-100',
      };
    return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getSuggestions = (type, score, data = null) => {
    if (!analysisData) {
      // Default suggestions when no data is available
      const defaultSuggestions = {
        scraping: [
          'Ensure website is accessible and loads properly',
          'Fix any broken links or missing resources',
          'Optimize server response times',
        ],
        seoAnalysis: [
          'Add missing meta descriptions',
          'Optimize title tags for better keywords',
          'Improve heading structure (H1, H2, H3)',
          'Add alt text to images',
        ],
        llmCrawlScore: [
          'Create or update llms.txt file',
          'Add structured data markup',
          'Improve content organization for AI understanding',
          'Include clear navigation structure',
        ],
        securityCheck: [
          'Implement Content Security Policy (CSP)',
          'Add security headers (HSTS, X-Frame-Options)',
          'Fix XSS vulnerabilities',
          'Update to HTTPS if not already implemented',
        ],
      };
      return defaultSuggestions[type] || [];
    }

    // Use real suggestions from API response
    return analysisData[type]?.suggestions || [];
  };

  const getConcreteReasons = (type) => {
    if (!analysisData || !analysisData[type]) return [];
    const node = analysisData[type];
    const details = node.details || node.metrics || {};

    switch (type) {
      case 'scraping':
        return [
          details.content_length !== undefined ? `Content length: ${details.content_length} bytes` : null,
          details.links_found !== undefined ? `Links found: ${details.links_found}` : null,
          details.images_found !== undefined ? `Images found: ${details.images_found}` : null,
        ].filter(Boolean);
      case 'seoAnalysis':
        return [
          details.title_length ? `Title length: ${details.title_length}` : null,
          details.meta_description_length ? `Meta description: ${details.meta_description_length}` : null,
          details.h1_count ? `H1 tags: ${details.h1_count}` : null,
          details.has_h2 !== undefined ? `Has H2: ${details.has_h2 ? 'Yes' : 'No'}` : null,
          details.image_alt_ratio ? `Image alt ratio: ${details.image_alt_ratio}` : null,
          details.word_count !== undefined ? `Word count: ${details.word_count}` : null,
          details.internal_links !== undefined ? `Internal links: ${details.internal_links}` : null,
          details.external_links !== undefined ? `External links: ${details.external_links}` : null,
          Array.isArray(details.meta_keywords) ? `Meta keywords: ${details.meta_keywords.length}` : null,
        ].filter(Boolean);
      case 'llmCrawlScore':
        return [
          details.has_llms_txt !== undefined ? `llms.txt present: ${details.has_llms_txt ? 'Yes' : 'No'}` : null,
          details.has_structured_data !== undefined ? `Structured data: ${details.has_structured_data ? 'Yes' : 'No'}` : null,
          details.semantic_html && Array.isArray(details.semantic_html.found_tags)
            ? `Semantic tags found: ${details.semantic_html.found_tags.join(', ') || 'None'}`
            : null,
          details.content_structure && details.content_structure.heading_structure
            ? `Headings: H1 ${details.content_structure.heading_structure.h1}, H2 ${details.content_structure.heading_structure.h2}, H3 ${details.content_structure.heading_structure.h3}`
            : null,
        ].filter(Boolean);
      case 'securityCheck':
        return [
          details.https !== undefined ? `HTTPS: ${details.https ? 'Enabled' : 'Not enabled'}` : null,
          details.security_headers ? `Security headers set: ${Object.values(details.security_headers).filter(v => v !== 'missing').length}` : null,
          details.csp && details.csp.csp_header ? 'CSP present' : 'CSP missing',
          details.xss_check && details.xss_check.potential_xss_vectors && details.xss_check.potential_xss_vectors.length > 0
            ? `Potential XSS vectors: ${details.xss_check.potential_xss_vectors.length}`
            : null,
          details.form_security ? `Forms: ${details.form_security.form_count}, Password fields: ${details.form_security.password_fields}` : null,
          details.mixed_content && details.mixed_content.mixed_content_found ? 'Mixed content detected' : null,
        ].filter(Boolean);
      default:
        return [];
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 flex-1">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Website Checks
              <br />
              <span className="bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent">
                Complete Analysis
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Comprehensive website analysis including scraping, SEO, LLM compatibility,
              and security checks with real-time progress tracking
            </p>
          </div>

          {/* Input Section */}
          <div className="max-w-4xl mx-auto">
            <Card className="mb-8 border border-border shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Website Checks
                </CardTitle>
                <CardDescription>
                  Enter your website URL to start comprehensive checks including scraping, SEO, LLM compatibility, and security analysis
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleAnalyze();
                }}>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Website URL
                    </label>
                    <Input
                      placeholder="https://rvish.com"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      className="w-full"
                    />
                    <p className="text-sm text-muted-foreground mt-1">
                      Enter a domain name (we'll automatically add https:// if needed)
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 mt-4"
                    disabled={!websiteUrl.trim() || isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Running Checks...
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-4 w-4" />
                        Start Website Checks
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Current Step Display */}
            {isAnalyzing && currentStep && (
              <Card className="mb-6 border border-blue-200 bg-blue-50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                    <span className="text-blue-800 font-medium">{currentStep}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Results Section */}
            {showResults && (
              <div className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Website Scraping */}
                  <Card className="border border-border shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Globe className="w-5 h-5 text-blue-600" />
                        Website Scraping
                        {!isAnalyzing &&
                          (() => {
                            const status = getScoreStatus(results.scraping);
                            const StatusIcon = status.icon;
                            return (
                              <div
                                className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center ml-auto`}
                              >
                                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                              </div>
                            );
                          })()}
                      </CardTitle>
                      <CardDescription>
                        Website accessibility and content extraction
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span className="font-medium">
                            {results.scraping}%
                          </span>
                        </div>
                        <Progress value={results.scraping} className="h-3" />
                        <div className="text-xs text-muted-foreground">
                          Extracting content, structure, and metadata
                        </div>
                        {!isAnalyzing && analysisData?.scraping?.details && (
                          <div className="mt-3 text-xs">
                            <p className="font-medium mb-1">Why this score</p>
                            <ul className="space-y-1 list-disc pl-5">
                              {getConcreteReasons('scraping').map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* SEO Analysis */}
                  <Card className="border border-border shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Eye className="w-5 h-5 text-purple-600" />
                        SEO Analysis
                        {!isAnalyzing &&
                          (() => {
                            const status = getScoreStatus(results.seoAnalysis);
                            const StatusIcon = status.icon;
                            return (
                              <div
                                className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center ml-auto`}
                              >
                                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                              </div>
                            );
                          })()}
                      </CardTitle>
                      <CardDescription>
                        Meta keywords, SEO factors, and optimization analysis
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span className="font-medium">{results.seoAnalysis}%</span>
                        </div>
                        <Progress value={results.seoAnalysis} className="h-3" />
                        <div className="text-xs text-muted-foreground">
                          Analyzing meta tags, keywords, and SEO factors
                        </div>
                        {!isAnalyzing && analysisData?.seoAnalysis?.details && (
                          <div className="mt-3 text-xs">
                            <p className="font-medium mb-1">Why this score</p>
                            <ul className="space-y-1 list-disc pl-5">
                              {getConcreteReasons('seoAnalysis').map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* LLM Crawl Score */}
                  <Card className="border border-border shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Bot className="w-5 h-5 text-green-600" />
                        LLM Crawl Score
                        {!isAnalyzing &&
                          (() => {
                            const status = getScoreStatus(results.llmCrawlScore);
                            const StatusIcon = status.icon;
                            return (
                              <div
                                className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center ml-auto`}
                              >
                                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                              </div>
                            );
                          })()}
                      </CardTitle>
                      <CardDescription>
                        LLM compatibility and llms.txt file analysis
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span className="font-medium">{results.llmCrawlScore}%</span>
                        </div>
                        <Progress value={results.llmCrawlScore} className="h-3" />
                        <div className="text-xs text-muted-foreground">
                          Comparing with llms.txt and AI readiness
                        </div>
                        {!isAnalyzing && analysisData?.llmCrawlScore?.details && (
                          <div className="mt-3 text-xs">
                            <p className="font-medium mb-1">Why this score</p>
                            <ul className="space-y-1 list-disc pl-5">
                              {getConcreteReasons('llmCrawlScore').map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Security Check */}
                  <Card className="border border-border shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg">
                        <Shield className="w-5 h-5 text-red-600" />
                        Security Check
                        {!isAnalyzing &&
                          (() => {
                            const status = getScoreStatus(results.securityCheck);
                            const StatusIcon = status.icon;
                            return (
                              <div
                                className={`w-6 h-6 rounded-full ${status.bg} flex items-center justify-center ml-auto`}
                              >
                                <StatusIcon className={`w-4 h-4 ${status.color}`} />
                              </div>
                            );
                          })()}
                      </CardTitle>
                      <CardDescription>
                        XSS vulnerabilities and security assessment
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span className="font-medium">{results.securityCheck}%</span>
                        </div>
                        <Progress value={results.securityCheck} className="h-3" />
                        <div className="text-xs text-muted-foreground">
                          Scanning for XSS and security vulnerabilities
                        </div>
                        {!isAnalyzing && analysisData?.securityCheck?.details && (
                          <div className="mt-3 text-xs">
                            <p className="font-medium mb-1">Why this score</p>
                            <ul className="space-y-1 list-disc pl-5">
                              {getConcreteReasons('securityCheck').map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Improvement Suggestions */}
                {!isAnalyzing && (
                  <Card className="border border-border shadow-sm">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-600" />
                        Improvement Suggestions
                      </CardTitle>
                      <CardDescription>
                        Actionable recommendations to boost your scores
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            Website Scraping ({results.scraping}%)
                          </h4>
                          <ul className="space-y-2 text-sm">
                            {getSuggestions('scraping', results.scraping).map(
                              (suggestion, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 flex-shrink-0" />
                                  {suggestion}
                                </li>
                              )
                            )}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Eye className="w-4 h-4 text-purple-600" />
                            SEO Analysis ({results.seoAnalysis}%)
                          </h4>
                          <ul className="space-y-2 text-sm">
                            {getSuggestions('seoAnalysis', results.seoAnalysis).map(
                              (suggestion, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0" />
                                  {suggestion}
                                </li>
                              )
                            )}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Bot className="w-4 h-4 text-green-600" />
                            LLM Crawl Score ({results.llmCrawlScore}%)
                          </h4>
                          <ul className="space-y-2 text-sm">
                            {getSuggestions('llmCrawlScore', results.llmCrawlScore).map(
                              (suggestion, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0" />
                                  {suggestion}
                                </li>
                              )
                            )}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-red-600" />
                            Security Check ({results.securityCheck}%)
                          </h4>
                          <ul className="space-y-2 text-sm">
                            {getSuggestions('securityCheck', results.securityCheck).map(
                              (suggestion, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 bg-red-600 rounded-full mt-2 flex-shrink-0" />
                                  {suggestion}
                                </li>
                              )
                            )}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {!showResults && !isAnalyzing && (
              <div className="text-center py-16">
                <Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Ready for Website Checks</h3>
                <p className="text-muted-foreground">
                  Enter a website URL above to start comprehensive checks including scraping, SEO, LLM compatibility, and security analysis
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteChecks;
